import { service } from '@ember/service';
import { sortGradesByOrder } from '../../../utils/sort-grades-by-order.ts';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import {
  getAllValuesForTasField,
  getCustomFieldObjectsForProvince,
  getConfigForTasField,
  getSortedOptionsByFieldName,
  getSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import AddCustomField from './add-custom-field.gts';
import ApprovalLayoutReview from './approval-workflow/approval-layout-review.gts';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import TableLayout from './table-layout.gts';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import TermsAndConditionsTable from './terms-and-conditions-table.gts';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import type { Enum as EnumModel } from 'tio-data/data/resources/enum.type';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import { Section } from 'tio-ui/components/layout';

export interface TuitionAssistanceProgramTemplateReviewCourseApplicationSetupSignature {
  Args: {
    model: TasProgramTemplateModel;
  };
}

export default class TuitionAssistanceProgramTemplateReviewCourseApplicationSetupComponent extends Component<TuitionAssistanceProgramTemplateReviewCourseApplicationSetupSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  getAllValuesForTasField = getAllValuesForTasField;
  getSortedOptionsByFieldName = getSortedOptionsByFieldName;

  getConfigForTasField = (fieldName: TemplateFieldName, fieldsObject: typeof this.reviewFields) => {
    const config = getConfigForTasField(fieldName, fieldsObject);
    // Provide default label for COST_PER_CREDIT if missing (legacy templates)
    if (fieldName === 'COST_PER_CREDIT' && !config.label) {
      return { ...config, label: 'Cost Per Credit' };
    }
    return config;
  };

  get isAllowedWaiverOfEvidenceApproval() {
    const config = getConfigForTasField(
      'ALLOW_WAIVER_OF_EVIDENCE_APPROVAL',
      this.args.model.fields
    );
    return config.value ?? false;
  }

  get reviewFields() {
    return this.args.model.fields || {};
  }

  get customReviewFields() {
    return this.args.model.customFields || {};
  }

  get courseInfoInputs(): TemplateFieldName[] {
    return [
      'COURSE_NAME',
      'COURSE_NUMBER',
      'COURSE_DESCRIPTION',
      'COURSE_CREDIT',
      'COST_PER_CREDIT',
      'COURSES_BEGIN_DATE',
      'COURSES_END_DATE',
      'COURSES_RECEIPT_DATE',
      'COURSE_GRADE',
      'COURSE_TUITION',
      'COURSE_BOOKS',
      'COURSE_LABS',
      'COURSE_SOFTWARE',
      'COURSE_FEES',
      'SCHOLARSHIPS_RECEIVED',
      'SCHOLARSHIP_NAME',
      'SCHOLARSHIP_AMOUNT',
      'SEMESTER_CODE',
    ];
  }

  get coursesWorkflowAttachmentAndList() {
    const attachmentString = getConfigForTasField(
      'COURSE_APPROVAL_DOCUMENTS_STRING',
      this.reviewFields
    );
    const attachmentList = getConfigForTasField(
      'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
      this.reviewFields
    );

    return {
      text: attachmentString?.value,
      values: attachmentList.values,
      visible: attachmentString?.visible,
      required: attachmentString?.required,
    };
  }

  get completionWorkflowAttachmentAndList() {
    const attachmentString = getConfigForTasField('COMPLETION_DOCUMENTS_STRING', this.reviewFields);
    const attachmentList = getConfigForTasField(
      'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
      this.reviewFields
    );

    return {
      text: (attachmentString?.value as string) || '',
      values: attachmentList.values,
      visible: attachmentString?.visible || false,
      required: attachmentString?.required || false,
    };
  }

  get customFieldsForCourseProvince() {
    return getCustomFieldObjectsForProvince('COURSE', this.customReviewFields);
  }

  get customFieldsForApplicationProvince() {
    return getCustomFieldObjectsForProvince('APPLICATION', this.customReviewFields);
  }

  get courseApproverResponses(): { [key: string]: TemplateFieldName } {
    return {
      response_template_for_approval: 'COURSES_APPROVAL_APPROVE_TEXT',
      response_template_for_rejection: 'COURSES_APPROVAL_REJECT_TEXT',
      response_template_for_revision: 'COURSES_APPROVAL_REVISE_TEXT',
    };
  }

  get evidenceApproverResponses(): { [key: string]: TemplateFieldName } {
    return {
      response_template_for_approval: 'EVIDENCE_APPROVAL_APPROVE_TEXT',
      response_template_for_rejection: 'EVIDENCE_APPROVAL_REJECT_TEXT',
      response_template_for_revision: 'EVIDENCE_APPROVAL_REVISE_TEXT',
    };
  }

  get exclusiveGradeOptions() {
    return getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.args.model.fields
    ) as string[];
  }

  get defaultGradeOptions() {
    return (this.defaultGradeOptionsData.value || []).map((model: EnumModel) => model.name);
  }

  get gradeOptions() {
    return this.exclusiveGradeOptions.length
      ? sortGradesByOrder(this.exclusiveGradeOptions).join(', ')
      : sortGradesByOrder(this.defaultGradeOptions).join(', ');
  }

  defaultGradeOptionsData = trackedFunction(this, async () => {
    // WarpDrive builder read (Phase 2.4) — replaces store.query; flattened
    // bracket key satisfies the builder's QueryParamsSource type.
    const { content } = (await this.store.request(
      query<EnumModel>('enum', { 'filter[flavor]': 'AcademicPerformance' })
    )) as { content: { data: EnumModel[] } };
    return content.data;
  });

  get passingGrades() {
    return sortGradesByOrder(
      this.getAllValuesForTasField('PASSING_ACADEMIC_PERFORMANCES', this.reviewFields) as string[]
    ).join(', ');
  }

  get expenseType() {
    return getSingleValueForTasField('EXPENSE_TYPE', this.reviewFields) as string;
  }

  formatMoney = (cents: number | null | undefined) => {
    if (cents === null || cents === undefined) {
      return 'None';
    }
    const dollars = cents / 100;

    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section>
      <:header>
        {{t "common.tuition_assistance.program_template_review.custom_fields_for_application"}}
      </:header>
      <:body>
        <AddCustomField @showCustomField={{true}} as |table|>
          {{#each this.customFieldsForApplicationProvince as |model|}}
            <table.customRowReview @model={{model}} />
          {{/each}}
        </AddCustomField>
        <h2 class="tio-text-heading mb-1 uppercase pt-6">
          {{t "common.tuition_assistance.program_template_review.courses_application_setup"}}
        </h2>
        <hr class="pb-2" />

        <dl class="flex mb-2">
          <dt class="mr-1 tio-fg-secondary">
            {{t "common.tuition_assistance.program_template_review.exclusive_grades"}}:
          </dt>
          <dd class="font-medium">{{this.gradeOptions}}</dd>
        </dl>
        <dl class="flex mb-2">
          <dt class="mr-1 tio-fg-secondary">
            {{t "common.tuition_assistance.program_template_review.passing_grades"}}:
          </dt>
          <dd class="font-medium">{{this.passingGrades}}</dd>
        </dl>
        <div class="flex mb-2">
          <p class="mr-1 tio-fg-secondary">
            {{t "common.tuition_assistance.program_template_review.expense_type"}}:
          </p>
          <p class="font-medium">{{this.expenseType}}</p>
        </div>
        <div class="pb-5 ml-2">
          <TableLayout
            @title={{t "common.tuition_assistance.program_template_review.courses_information"}}
            as |table|
          >
            {{#each this.courseInfoInputs as |fieldName|}}
              <table.rowReview
                @field={{this.getConfigForTasField fieldName this.reviewFields}}
                @fieldOptions={{this.getSortedOptionsByFieldName fieldName @model.tasFieldOptions}}
              />
            {{/each}}
          </TableLayout>

          <AddCustomField @showCustomField={{true}} as |table|>
            {{#each this.customFieldsForCourseProvince as |model|}}
              <table.customRowReview @model={{model}} />
            {{/each}}
          </AddCustomField>

          <ApprovalLayoutReview
            @workflowStepName={{t
              "common.tuition_assistance.program_template_review.courses_pre_approval"
            }}
            @reviewFields={{this.reviewFields}}
            @workflowStepType={{t "common.application"}}
            @approvalWorkflowName="APPLICATION_APPROVE_COURSES_WORKFLOW"
            {{! @glint-expect-error: Type 'FieldValue[]' is not assignable to type 'TemplateApprovalWorkflowApprover[]'.}}
            @approverList={{this.getAllValuesForTasField
              "APPLICATION_APPROVE_COURSES_WORKFLOW"
              this.reviewFields
            }}
            {{! @glint-expect-error: Rework this so the types are more easily parsed by ApprovalLayoutReview}}
            @attachmentData={{this.coursesWorkflowAttachmentAndList}}
            @cannedApproverResponses={{this.courseApproverResponses}}
            @tasFieldOptions={{@model.tasFieldOptions}}
          />

          <h2 class="font-bold mt-8">
            {{t "common.terms_and_conditions"}}
          </h2>
          <TermsAndConditionsTable @conditions={{@model.applicationApproveCoursesConditions}} />

          <ApprovalLayoutReview
            @workflowStepName={{t
              "common.tuition_assistance.program_template_review.payment_review_and_final_approval"
            }}
            @reviewFields={{this.reviewFields}}
            @workflowStepType={{t "common.application"}}
            @approvalWorkflowName="APPLICATION_APPROVE_EVIDENCE_WORKFLOW"
            {{! @glint-expect-error: Type 'FieldValue[]' is not assignable to type 'TemplateApprovalWorkflowApprover[]'.}}
            @approverList={{this.getAllValuesForTasField
              "APPLICATION_APPROVE_EVIDENCE_WORKFLOW"
              this.reviewFields
            }}
            {{! @glint-expect-error: Rework this so the types are more easily parsed by ApprovalLayoutReview}}
            @attachmentData={{this.completionWorkflowAttachmentAndList}}
            @cannedApproverResponses={{this.evidenceApproverResponses}}
            @tasFieldOptions={{@model.tasFieldOptions}}
          />

          <div class="my-8">
            <h2 class="tio-text-sm-semibold uppercase mb-2">
              {{t "common.tuition_assistance.approval_waiver_config.approval_workflow_waiver"}}
            </h2>
            <p class="mb-4">
              <span class="font-semibold">
                {{t "common.tuition_assistance.approval_waiver_config.allow_approval_waiver"}}
              </span>
              {{if this.isAllowedWaiverOfEvidenceApproval (t "common.yes") (t "common.no")}}
            </p>
            {{#if this.isAllowedWaiverOfEvidenceApproval}}
              <p class="font-semibold mb-2">
                {{t
                  "common.tuition_assistance.approval_waiver_config.checklist_for_attachment_waiver"
                }}
              </p>
              <ol class="pl-4 list-disc list-inside">
                {{#each
                  (this.getAllValuesForTasField
                    "EVIDENCE_ATTACHMENT_WAIVER_CHECKLIST" @model.fields
                  )
                  as |condition|
                }}
                  {{! @glint-expect-error: no primitives - need to better type field values}}
                  <li class="mb-1">{{condition}}</li>
                {{/each}}
              </ol>
              <p class="font-semibold mt-4 mb-2">
                {{t
                  "common.tuition_assistance.approval_waiver_config.checklist_for_approval_waiver"
                }}
              </p>
              <ol class="pl-4 list-disc list-inside">
                {{#each
                  (this.getAllValuesForTasField "EVIDENCE_APPROVAL_WAIVER_CHECKLIST" @model.fields)
                  as |condition|
                }}
                  {{! @glint-expect-error: no primitives - need to better type field values}}
                  <li class="mb-1">{{condition}}</li>
                {{/each}}
              </ol>
            {{/if}}
          </div>

          <h2 class="tio-text-sm-semibold uppercase">
            {{t "common.terms_and_conditions"}}
          </h2>
          <TermsAndConditionsTable @conditions={{@model.applicationApproveEvidenceConditions}} />
        </div>
      </:body>
    </Section>
  </template>
}
